import { attr, belongsTo, hasMany } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";
import type ConsultationPlannedJournalEvent from "./consultation-planned-journal-event";
import type OkChimioSnapshotCapturedJournalEvent from "./ok-chimio-snapshot-captured-journal-event";
import type Protocol from "./protocol";
import type RecursiveTaskPlannedJournalEvent from "./recursive-task-planned-journal-event";
import type TaskPlannedJournalEvent from "./task-planned-journal-event";

export interface Surgery {
  uuid: number;
  consultation: Date;
  date: Date;
  types: Array<string>;
  reconstructionType: Array<string>;
  areolaConservation: boolean;
  postConsultation: Date;
}

export interface Chemotherapy {
  therapyOne: Date;
  therapyTwo?: Date;
  therapyThree?: Date;
  therapyFour?: Date;
  therapyFive?: Date;
  lastTherapy?: Date;
  protocol?: string;
}

export interface Oncology {
  consultation: Date;
}

export interface Radiotherapy {
  startDate: Date;
  endDate: Date;
  consultation: Date;
}

export interface Immunotherapy {
  startDate: Date;
  endDate: Date;
  consultation: Date;
}

export interface TargetedTherapy {
  startDate: Date;
  endDate: Date;
  type: Array<string>;
}

export interface Hormonotherapy {
  startDate: Date;
  endDate?: Date;
  type?: string;
}

export interface OtherFollowUpEvent {
  type: string;
  date: Date;
}

//---- Automatically generated, don't update this part ----

export default class TreatmentHistoryChangedJournalEvent extends JournalEvent {
  static type = "treatment-history-changed-journal-event";

  static validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.treatment-history-changed-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ]
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "treatmentHistoryChangedJournalEvent"
  })
  declare patient: Patient;

  @hasMany("task-planned-journal-event", {
    async: false,
    inverse: "treatmentHistoryChangedJournalEvent"
  })
  declare taskPlannedJournalEvents: TaskPlannedJournalEvent[];

  @hasMany("recursive-task-planned-journal-event", {
    async: false,
    inverse: "treatmentHistoryChangedJournalEvent"
  })
  declare recursiveTaskPlannedJournalEvents: RecursiveTaskPlannedJournalEvent[];

  @hasMany("consultation-planned-journal-event", {
    async: false,
    inverse: "treatmentHistoryChangedJournalEvent"
  })
  declare consultationPlannedJournalEvents: ConsultationPlannedJournalEvent[];

  @hasMany("ok-chimio-snapshot-captured-journal-event", {
    async: false,
    inverse: "treatmentHistoryChangedJournalEvent"
  })
  declare okChimioSnapshotCapturedJournalEvents: OkChimioSnapshotCapturedJournalEvent[];

  @attr("surgery-surgeries")
  declare surgerySurgeries: Surgery[];

  get surgery(): boolean {
    return (
      this.surgerySurgeries !== undefined && this.surgerySurgeries.length > 0
    );
  }

  get lastSurgery(): Surgery {
    return this.lastElement(this.surgerySurgeries, "startDate");
  }

  @attr("protocols")
  declare protocols: Protocol[];

  @attr("chemotherapies")
  declare chemotherapies: Chemotherapy[];

  get chemotherapy(): boolean {
    return this.chemotherapies !== undefined && this.chemotherapies.length > 0;
  }

  get lastChemotherapy(): Chemotherapy {
    return this.lastElement(this.chemotherapies, "therapyOne");
  }

  @attr("oncologies")
  declare oncologies: Oncology[];

  get oncology(): boolean {
    return this.oncologies !== undefined && this.oncologies.length > 0;
  }

  @attr("radiotherapies")
  declare radiotherapies: Radiotherapy[];

  get radiotherapy(): boolean {
    return this.radiotherapies !== undefined && this.radiotherapies.length > 0;
  }

  get lastRadiotherapy(): Radiotherapy {
    return this.lastElement(this.radiotherapies, "startDate");
  }

  @attr("immunotherapies")
  declare immunotherapies: Immunotherapy[];

  get hasImmunotherapy(): boolean {
    return (
      this.immunotherapies !== undefined && this.immunotherapies.length > 0
    );
  }

  get lastImmunotherapy(): Immunotherapy {
    return this.lastElement(this.immunotherapies, "startDate");
  }

  @attr("targeted-therapies")
  declare targetedTherapies: TargetedTherapy[];

  get targetedTherapy(): boolean {
    return (
      this.targetedTherapies !== undefined && this.targetedTherapies.length > 0
    );
  }

  get lastTargetedtherapy(): TargetedTherapy {
    return this.lastElement(this.targetedTherapies, "startDate");
  }

  @attr("hormonotherapies")
  declare hormonotherapies: Hormonotherapy[];

  get hormonotherapy(): boolean {
    return (
      this.hormonotherapies !== undefined && this.hormonotherapies.length > 0
    );
  }

  get lastHormonotherapy(): Hormonotherapy {
    return this.lastElement(this.hormonotherapies, "startDate");
  }

  @attr("other-follow-up-events")
  declare otherFollowUpEvents: OtherFollowUpEvent[];

  get otherFollowUp(): boolean {
    return (
      this.otherFollowUpEvents !== undefined &&
      this.otherFollowUpEvents.length > 0
    );
  }

  /*
   * Remove every task and consultations associated to this JE from the store (without a push to the API) before fetching them
   */
  async clearAndFetchAssociatedTaskAndConsultation(): Promise<void> {
    // In fact, the back will not push archived JEs
    while (this.taskPlannedJournalEvents.length > 0) {
      this.taskPlannedJournalEvents[0].unloadRecord();
    }
    while (this.consultationPlannedJournalEvents.length > 0) {
      this.consultationPlannedJournalEvents[0].unloadRecord();
    }
    while (this.recursiveTaskPlannedJournalEvents.length > 0) {
      this.recursiveTaskPlannedJournalEvents[0].unloadRecord();
    }

    await this.store.query("treatment-history-changed-journal-event", {
      filter: { id: this.id },
      include: [
        "task-planned-journal-events",
        "task-planned-journal-events.task-processed-journal-event",
        "recursive-task-planned-journal-events",
        "consultation-planned-journal-events"
      ].join(",")
    });
  }

  get activeProtocols(): Protocol[] {
    return this.protocols.filter(p => p.isActive);
  }

  get pausedProtocols(): Protocol[] {
    return this.protocols.filter(p => p.isPaused);
  }

  get pausedAndActiveProtocols(): Protocol[] {
    return this.protocols.filter(p => p.isActive || p.isPaused);
  }

  get nextProtocol(): Protocol | undefined {
    const today = dayjs();
    let nextProtocol;
    let nextCureDiff = Infinity;

    this.protocols
      .filter(p => p.treatmentProtocolType == "cure")
      .forEach(p => {
        const nextCureDate = p.nextCureDate;
        if (!nextCureDate) return;

        const diff = dayjs(nextCureDate).diff(today);
        if (diff < nextCureDiff) {
          nextProtocol = p;
          nextCureDiff = diff;
        }
      });

    return nextProtocol;
  }

  getProtocol(protocolUuid?: string): Protocol | undefined {
    if (!protocolUuid) return undefined;
    return this.protocols.find(protocol => protocol.uuid == protocolUuid);
  }

  get specificProtocols(): Protocol[] {
    return this.protocols.filter(p => !p.isDefaultProtocol);
  }

  get okChimioMustBeValidated(): Protocol[] {
    const res = this.specificProtocols.filter(p => {
      const cureKey = p.cureKeyInOkChimioValidationPeriod;
      return (
        cureKey && p.uuid && !this.protocolCureHasSnapshot(p.uuid, cureKey)
      );
    });
    return res;
  }

  protocolCureHasSnapshot(protocolUuid: string, cureKey: string): boolean {
    if (!cureKey) return false;
    return Boolean(
      this.okChimioSnapshotCapturedJournalEvents.find(
        okChimio =>
          okChimio.protocolUuid == protocolUuid && okChimio.cureKey == cureKey
      )
    );
  }

  private lastElement(array, keyToCompare) {
    return array.sort((a, b) =>
      a[keyToCompare] < b[keyToCompare] ? 1 : -1
    )[0];
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "treatment-history-changed-journal-event": TreatmentHistoryChangedJournalEvent;
  }
}
